<template>
  <div class="flex-1 flex flex-col justify-between">
    <div class="divide-y divide-gray-200">
      <div class="space-y-6 pt-6 pb-5">
        <Form
          v-if="!add_new"
          :initial-values="initial_values"
          :validation-schema="schema"
          class="mt-2 space-y-6"
          @submit="updateExpansionList">
          <ul role="list" class="flex-1 divide-y divide-gray-200 overflow-y-auto">
            <li v-for="list in expansion_lists" :key="list.id">
              <div class="relative py-6 px-5 flex items-center">
                <div class="-m-1 flex-1 block p-1">
                  <div class="flex-1 flex items-center min-w-0 relative">
                    <div class="ml-4 truncate">
                      <p class="text-sm font-medium dark:text-dark-body-text text-gray-900 truncate">
                        {{ list.name }}
                      </p>
                      <p class="text-sm dark:text-dark-body-text text-gray-500 truncate">
                        {{ list.description }}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  class="bg-white text-gray-400 group hover:text-gray-900 hover:bg-gray-200 p-2 hover:shadow-sm rounded-full"
                  @click="updateExpansionList(list)">
                  <span class="sr-only">{{ $t("Close panel") }}</span>
                  <Check class="h-6 w-6 group-hover:text-gray-900" aria-hidden="true" />
                </button>
              </div>
            </li>
          </ul>
          <div class="flex justify-center items-center w-full">
            <div
              type="button"
              class="w-1/2 text-center cancel-btn border-none"
              @click.prevent="
                () => {
                  add_new = true
                }
              ">
              {{ $t("Add New List") }}
            </div>
          </div>
        </Form>
        <Form v-else
              :initial-values="initial_values"
              :validation-schema="schema"
              class="mt-2 space-y-6"
              @submit="createExpansionList">
          <h1 class="dark:text-dark-body-text">
            {{ $t("Create list from selection") }}
          </h1>
          <div class="rounded-md shadow-sm -space-y-px">
            <TextInput
              :label="$t('List Name')"
              :show-label="true"
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="name"
              type="text" />
          </div>
          <div class="rounded-md shadow-sm -space-y-px">
            <TextArea
              :label="$t('Description')"
              :show-label="true"
              input-classes="h-48 resize-none appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="description"
              type="text" />
          </div>
          <div class="flex gap-2 w-full flex-row justify-between">
            <div
              class="cancel-btn w-1/2 m-0"
              @click="
                () => {
                  add_new = false
                }
              ">
              {{ $t("Cancel") }}
            </div>
            <button type="submit" class="primary-btn w-1/2">{{ $t("Add") }}</button>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script>
import * as Yup from "yup"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import { Form } from "vee-validate"
import dataService from "@/services/data.service"
import { Check } from "lucide-vue-next"
import { useI18n } from "vue-i18n"

export default {
  components: {
    Check,
    TextInput,
    Form,
    TextArea
  },
  props: {
    records: {
      type: Array,
      required: true
    },
    filter: {
      type: Object,
      required: false
    },
    objectType: {
      type: String,
      required: false
    },
    excludedRecords: {
      type: Array,
      default: () => []
    },
    isAllRecords: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel", "submitted", "done"],
  setup(props) {
    // Using yup to generate a validation schema
    // https://vee-validate.logaretm.com/v4/guide/validation#validation-schemas-with-yup
    const { t } = useI18n()
    const schema = Yup.object().shape({
      name: Yup.string().required(t("List name is required")),
      description: Yup.string(),
      records: Yup.array().of(Yup.string())
    })
    const initial_values = { name: "", description: "", records: props.records }

    return {
      schema,
      initial_values
    }
  },
  data() {
    return {
      add_new: false,
      expansion_lists: [],
      object_type: null
    }
  },

  created() {
    dataService.getExpansionLists().then((result) => {
      if (this.records?.length > 0) {
        this.$store.dispatch("data/getRecord", this.records[0]).then((res) => {
          this.object_type = res.data.type
          this.expansion_lists = result.data.results.filter((list) => list.type == this.object_type)
        })
      } else if (this.isAllRecords) {
        this.object_type = this.objectType
        this.expansion_lists = result.data.results.filter((list) => list.type == this.object_type)
      }
    })
  },

  methods: {
    createExpansionList(values) {
      let data = { ...values }
      if (this.isAllRecords) {
        data["is_all_records"] = true
        //data["object_type"] = this.objectType
        data["object_type"] = this.$utils.getObjectTypeByType(this.objectType).id
        data["excluded_records"] = this.excludedRecords
        data["filter"] = this.filter
      }

      dataService.createExpansionList(data).then(() => {
        dataService.getExpansionLists().then((result) => {
          this.expansion_lists = result.data.results.filter((list) => list.type == this.object_type)
          this.add_new = false
          this.$emit("done")
        })
      })
    },

    updateExpansionList(list) {
      let new_records_ids = this.records
      let new_list = this.$utils.clone(list.records)
      new_records_ids.forEach((id) => {
        if (!id.includes(new_list)) {
          new_list.push(id)
        }
      })
      dataService.updateExpansionList(list.id, {
        records: new_list,
        excluded_records: this.excludedRecords,
        object_type: this.objectType,
        is_all_records: this.isAllRecords
      })
      this.$emit("done")
    }
  }
}
</script>
